import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  acceptOwnerInvite,
  ApiError,
  InviteAcceptError,
  type InviteAcceptResponse,
} from '../lib/api.js';
import { PasswordInput } from '../components/PasswordInput.js';

// D1.7.2 — Owner invite acceptance page. The magic-link email (D1.7.1)
// points at `https://{slug}.admin.animalloversapp.com/invite/accept?token=...`.
// This page reads the token from the query string, asks the owner for a
// password, posts `{ token, password }` to `POST /api/platform/invites/accept`,
// and on success shows a "signed up — head to login" state with a link
// into the admin's `/login` page.
//
// Validation mirrors the server: 8-char minimum password, second field
// must match. The server is the source of truth for token validity,
// expiry, and already-accepted.

type InviteAcceptProps = {
  config: ClientConfig;
};

type UiState =
  | { kind: 'missing_token' }
  | { kind: 'ready' }
  | { kind: 'submitting' }
  | { kind: 'success'; outcome: InviteAcceptResponse }
  | { kind: 'error'; message: string; errorKind: string };

const MIN_PASSWORD = 8;

export function InviteAccept({ config }: InviteAcceptProps): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);
  const email = useMemo(() => searchParams.get('email') ?? '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<UiState>(() =>
    token.trim() === '' ? { kind: 'missing_token' } : { kind: 'ready' },
  );

  useEffect(() => {
    if (token.trim() === '') {
      setState({ kind: 'missing_token' });
    } else if (state.kind === 'missing_token') {
      setState({ kind: 'ready' });
    }
  }, [token, state.kind]);

  const clientFieldError = useMemo(() => {
    if (password.length > 0 && password.length < MIN_PASSWORD) {
      return `Password must be at least ${MIN_PASSWORD} characters.`;
    }
    if (confirm.length > 0 && confirm !== password) {
      return 'Passwords do not match.';
    }
    return null;
  }, [password, confirm]);

  const formReady =
    token.trim() !== '' &&
    password.length >= MIN_PASSWORD &&
    confirm === password;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formReady || state.kind !== 'ready') return;
    setState({ kind: 'submitting' });
    try {
      const outcome = await acceptOwnerInvite(config.deployment.apiUrl, {
        token,
        password,
      });
      setState({ kind: 'success', outcome });
      setPassword('');
      setConfirm('');
    } catch (err) {
      const { message, kind } = describeError(err);
      setState({ kind: 'error', message, errorKind: kind });
    }
  };

  if (state.kind === 'missing_token') {
    return (
      <main className="ferro-admin-login">
        <section className="ferro-auth">
          <h1 className="ferro-auth__title">Invite link is missing</h1>
          <p className="ferro-auth__subtitle">
            This page expects a token in the URL. Open the link from your
            invite email.
          </p>
        </section>
      </main>
    );
  }

  if (state.kind === 'success') {
    return (
      <main className="ferro-admin-login">
        <section className="ferro-auth">
          <h1 className="ferro-auth__title">You're in.</h1>
          <p className="ferro-auth__subtitle">
            {config.brand.name} is ready. Sign in with your new password.
          </p>
          <div className="ferro-form__success" role="status">
            Your password is saved.
          </div>
          <p className="ferro-auth__hint">
            After you sign in, you'll land on your home screen — {config.brand.name}
            's daily schedule and your work queue live there.
          </p>
          <a
            href={
              email
                ? `/login?email=${encodeURIComponent(email)}`
                : '/login'
            }
            className="ferro-btn ferro-btn--primary ferro-btn--block"
          >
            Continue to sign in
          </a>
        </section>
      </main>
    );
  }

  return (
    <main className="ferro-admin-login">
      <section className="ferro-auth">
        <h1 className="ferro-auth__title">Set your password</h1>
        <p className="ferro-auth__subtitle">
          Create a password for your {config.brand.name} admin account.
        </p>
        <form className="ferro-form" onSubmit={handleSubmit} noValidate>
          {email ? (
            <label className="ferro-field" htmlFor="invite-email">
              <span className="ferro-field__label">Email</span>
              <input
                id="invite-email"
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                readOnly
                className="ferro-field__input"
              />
              <span className="ferro-field__hint">
                The email this invite was sent to.
              </span>
            </label>
          ) : null}
          <label className="ferro-field" htmlFor="invite-password">
            <span className="ferro-field__label">New password</span>
            <PasswordInput
              id="invite-password"
              name="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={state.kind === 'submitting'}
            />
            <span className="ferro-field__hint">At least {MIN_PASSWORD} characters.</span>
          </label>
          <label className="ferro-field" htmlFor="invite-confirm-password">
            <span className="ferro-field__label">Confirm password</span>
            <PasswordInput
              id="invite-confirm-password"
              name="confirm"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={state.kind === 'submitting'}
            />
          </label>
          {clientFieldError ? (
            <p className="ferro-form__error" role="alert">
              {clientFieldError}
            </p>
          ) : null}
          {state.kind === 'error' ? (
            <p className="ferro-form__error" role="alert">
              {state.message}
            </p>
          ) : null}
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary ferro-btn--block"
            disabled={!formReady || state.kind !== 'ready'}
          >
            {state.kind === 'submitting' ? 'Setting password…' : 'Accept invite'}
          </button>
        </form>
      </section>
    </main>
  );
}

function describeError(err: unknown): { message: string; kind: string } {
  if (err instanceof InviteAcceptError) {
    const base = err.message;
    switch (err.kind) {
      case 'invalid_token':
        return {
          message:
            'This invite link is not valid. Ask your manager to resend the invite.',
          kind: err.kind,
        };
      case 'already_accepted':
        return {
          message:
            'This invite has already been accepted. Sign in with your password instead.',
          kind: err.kind,
        };
      case 'expired':
        // True as of A5 (SPEC-2026-07-09): owners/managers have a
        // "Resend invite" action on the Staff page — the copy points at
        // a door that actually exists now.
        return {
          message:
            'This invite link has expired. Ask your manager to resend the invite.',
          kind: err.kind,
        };
      case 'validation':
        return {
          message:
            err.body?.issues?.map((i) => i.message).join(' ') ??
            'Validation failed. Check your password and try again.',
          kind: err.kind,
        };
      case 'db_error':
        return {
          message: 'Something went wrong on our end. Please retry.',
          kind: err.kind,
        };
      default:
        return { message: base, kind: err.kind };
    }
  }
  if (err instanceof ApiError) {
    return { message: err.message, kind: 'api_error' };
  }
  if (err instanceof Error) {
    return { message: err.message, kind: 'unknown' };
  }
  return { message: 'Unexpected error', kind: 'unknown' };
}
